import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, MoreHorizontal, Shield, Users as UsersIcon, Loader2 } from "lucide-react";

import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import StatusBadge from "@/components/StatusBadge";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

export default function RolesList() {
  const { hasPermission } = useAuth();
  const canWrite = hasPermission("settings.roles.write");
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [counts, setCounts] = useState({}); // role_id → {total, active}

  const [showInactive, setShowInactive] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/settings/roles?active=${showInactive ? "all" : "true"}`);
      setRows(data);
      // Fire count lookups in parallel; ignore individual failures.
      const results = await Promise.all(
        data.map((r) => api.get(`/settings/roles/${r.id}/users-count`).then((res) => [r.id, res.data]).catch(() => [r.id, null]))
      );
      const map = {};
      for (const [id, val] of results) if (val) map[id] = val;
      setCounts(map);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [showInactive]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (row) => {
    if (!canWrite) return;
    const c = counts[row.id];
    if (c && c.active > 0) {
      toast.error(`Cannot delete — ${c.active} active user(s) still on this role`);
      return;
    }
    if (!window.confirm(`Delete role "${row.display_name}"? This is a soft-delete; you can re-create it by name later.`)) return;
    try {
      await api.delete(`/settings/roles/${row.id}`);
      toast.success("Role deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="p-6 space-y-6" data-testid="settings-roles-page">
      <PageHeader
        testId="settings-roles-header"
        title="Roles & Permissions"
        subtitle="Manage the roles users can be assigned and the permissions granted to each."
        actions={
          <>
            <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer" data-testid="settings-roles-show-inactive-toggle">
              <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
              Show inactive
            </label>
            {canWrite && (
              <Button onClick={() => navigate("/settings/roles/new")} data-testid="settings-roles-new-btn">
                <Plus className="h-4 w-4" /> New role
              </Button>
            )}
          </>
        }
      />

      <Card className="rounded-md">
        <CardContent className="p-0">
          <table className="w-full text-xs" data-testid="settings-roles-table">
            <thead className="bg-muted/40 border-y border-border">
              <tr>
                <Th>Name</Th>
                <Th>Display name</Th>
                <Th>Type</Th>
                <Th>Users</Th>
                <Th>Permissions</Th>
                <Th className="text-right">Actions</Th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="p-4 text-center text-muted-foreground">
                  <Loader2 className="h-4 w-4 inline animate-spin" /> Loading…
                </td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={6} className="p-4 text-center text-muted-foreground italic">No roles</td></tr>
              ) : (
                rows.map((row) => {
                  const cnt = counts[row.id] || { total: 0, active: 0 };
                  return (
                    <tr key={row.id} className="border-b border-border hover:bg-muted/20" data-testid={`settings-roles-row-${row.name}`}>
                      <td className="p-2">
                        <Link to={`/settings/roles/${row.id}`} className="font-mono font-medium text-primary hover:underline" data-testid={`settings-roles-row-name-${row.name}`}>
                          {row.name}
                        </Link>
                        {!row.active && <span className="ml-2 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Inactive</span>}
                      </td>
                      <td className="p-2">{row.display_name}</td>
                      <td className="p-2">
                        {row.is_system ? (
                          <StatusBadge tone="info" testId={`settings-roles-type-${row.name}`}>
                            <Shield className="h-3 w-3" /> System
                          </StatusBadge>
                        ) : (
                          <StatusBadge tone="muted" testId={`settings-roles-type-${row.name}`}>Custom</StatusBadge>
                        )}
                      </td>
                      <td className="p-2">
                        <span className="flex items-center gap-1"><UsersIcon className="h-3 w-3 text-muted-foreground" />{cnt.active}</span>
                      </td>
                      <td className="p-2 text-muted-foreground">{row.permissions.length}</td>
                      <td className="p-2 text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" data-testid={`settings-roles-actions-${row.name}`}>
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onSelect={() => navigate(`/settings/roles/${row.id}`)} data-testid={`settings-roles-action-view-${row.name}`}>
                              View / edit permissions
                            </DropdownMenuItem>
                            {canWrite && !row.is_system && (
                              <>
                                <DropdownMenuItem onSelect={() => navigate(`/settings/roles/${row.id}?reassign=1`)} data-testid={`settings-roles-action-reassign-${row.name}`}>
                                  Reassign users…
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  disabled={cnt.active > 0}
                                  onSelect={() => handleDelete(row)}
                                  className="text-destructive focus:text-destructive"
                                  data-testid={`settings-roles-action-delete-${row.name}`}
                                >
                                  Delete{cnt.active > 0 ? ` (${cnt.active} users)` : ""}
                                </DropdownMenuItem>
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}

function Th({ children, className = "" }) {
  return <th className={`text-left p-2 text-[10px] font-bold uppercase tracking-wider text-muted-foreground ${className}`}>{children}</th>;
}
